import * as types from 'modules/checklists/actionTypes';
import type { DeleteChecklistSuccessAction, FetchChecklistsSelectSuccessAction, FetchSelectedChecklistsSuccess } from 'modules/checklists/types';
import { uniq } from 'utils/ObjectUtils';
import { createReducer } from 'utils/ReduxUtils';

export type ChecklistDataByTypeReducer = Record<string, Record<string, string[]>>;

const initialState: ChecklistDataByTypeReducer = {};

const mergeChecklistType = (
  acc: ChecklistDataByTypeReducer,
  dealerId: string,
  type: string,
  id: string
): ChecklistDataByTypeReducer => ({
  ...acc,
  [dealerId]: {
    ...acc[dealerId],
    [type]: uniq([...(acc[dealerId]?.[type] ?? []), id]),
  },
});

const afterFetchChecklists = (
  state: ChecklistDataByTypeReducer,
  { payload }: FetchChecklistsSelectSuccessAction | FetchSelectedChecklistsSuccess
): ChecklistDataByTypeReducer =>
  payload.content.reduce<ChecklistDataByTypeReducer>((acc, item) => {
    // Prefer the item's own dealerId (always present, real, for FETCH_CHECKLISTS_SELECT_SUCCESS)
    // over the payload-level one: sagas.ts's fetchChecklistsSelect always spreads a `dealerId` key
    // onto that action's payload too (even when undefined), so `'dealerId' in payload` can't be
    // used to tell the two action shapes apart at runtime.
    const dealerId = item.dealerId ?? ('dealerId' in payload ? payload.dealerId : undefined);
    if (!dealerId) {
      return acc;
    }
    return mergeChecklistType(acc, dealerId, item.type, item.id);
  }, state);

const removeOne = (
  state: ChecklistDataByTypeReducer,
  { payload: checklistId }: DeleteChecklistSuccessAction
): ChecklistDataByTypeReducer =>
  Object.keys(state).reduce<ChecklistDataByTypeReducer>(
    (newState, dealerId) => ({
      ...newState,
      [dealerId]: Object.entries(state[dealerId]).reduce<Record<string, string[]>>(
        (acc, [type, values]) => ({
          ...acc,
          [type]: values.filter(id => id !== checklistId),
        }),
        state[dealerId]
      ),
    }),
    state
  );

export default createReducer(initialState, {
  [types.DELETE_CHECKLIST_SUCCESS]: removeOne,
  [types.FETCH_CHECKLISTS_SELECT_SUCCESS]: afterFetchChecklists,
  [types.FETCH_SELECTED_CHECKLISTS_SUCCESS]: afterFetchChecklists,
});
